import {ActionIcon, Button, Group, Tooltip} from '@mantine/core';
import {useMediaQuery, useTimeout} from '@mantine/hooks';
import {IconRefresh} from '@tabler/icons-react';
import type {
  MRT_ColumnFilterFnsState,
  MRT_ColumnFiltersState,
  MRT_ColumnOrderState,
  MRT_ColumnSizingState,
  MRT_DensityState,
  MRT_PaginationState,
  MRT_Row,
  MRT_RowData,
  MRT_RowSelectionState,
  MRT_SortingState,
  MRT_TableInstance,
  MRT_TableOptions,
  MRT_VisibilityState,
} from 'mantine-react-table';
import {
  getAllLeafColumnDefs,
  getColumnId,
  MantineReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  useMantineReactTable,
  type MRT_ColumnDef,
} from 'mantine-react-table';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type SetStateAction,
} from 'react';
import classes from './table.module.css';

/**
 * State-management + config hooks around `mantine-react-table`: `useTableState`
 * persists column filters/sorting/pagination/etc. to localStorage per table
 * `identifier`, and `useTable` wires that state (plus loading/error/refresh
 * props) into a configured `MantineReactTable` element.
 * @registry Server-driven MantineReactTable wrapper with localStorage-backed
 * column/filter/sort/pagination state and a built-in refresh toolbar action.
 *
 * Ported from `library-m/hooks/table/useTable`. Behavior and public exports
 * kept identical (`useTableState`, `useTableStateProps`, `useTable`,
 * `useTableProps`).
 */

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

const hydrateFilterValue = (value: unknown): unknown => {
  if (typeof value === 'string' && ISO_DATE_REGEX.test(value)) {
    return new Date(value);
  }
  if (Array.isArray(value)) {
    return value.map(hydrateFilterValue);
  }
  return value;
};

const serializeFilterValue = (value: unknown): unknown => {
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (Array.isArray(value)) {
    return value.map(serializeFilterValue);
  }
  return value;
};

const ID_SEPARATOR = '\u0000';

const toIdSet = (key: string) => new Set(key.split(ID_SEPARATOR));

const pruneById = <V extends {id: string}>(items: V[], ids: Set<string>) => {
  const kept = items.filter(item => ids.has(item.id));
  return kept.length === items.length ? items : kept;
};

const pruneFilterFns = (
  fns: MRT_ColumnFilterFnsState,
  ids: Set<string>
): MRT_ColumnFilterFnsState => {
  const entries = Object.entries(fns);
  const kept = entries.filter(([id]) => ids.has(id));
  return kept.length === entries.length ? fns : Object.fromEntries(kept);
};

const DATE_FILTER_MODE_OPTIONS = [
  'equals',
  'notEquals',
  'greaterThan',
  'lessThan',
  'greaterThanOrEqual',
  'lessThanOrEqual',
] as const;

/**
 * Moves focus between the filter row's visible inputs on Tab/Shift+Tab,
 * wrapping at either end. Exported (deviation from the frozen source, which
 * kept this private) purely so its edge-case branches — no table ancestor,
 * no thead, target not in the tracked input list — can be unit-tested
 * without contorting real DOM structure; it is not part of the hook's
 * intended public surface.
 */
export const handleFilterTabNavigation = (
  event: React.KeyboardEvent<HTMLInputElement>
) => {
  if (event.key !== 'Tab') return;

  const table = (event.target as HTMLElement).closest('table');
  const thead = table?.querySelector('thead');
  if (!thead) return;

  const inputs = Array.from(
    thead.querySelectorAll<HTMLInputElement>('input')
  ).filter(
    input =>
      input.offsetParent !== null &&
      !input.disabled &&
      input.type !== 'hidden' &&
      input.getAttribute('tabindex') !== '-1'
  );

  const currentIndex = inputs.indexOf(event.target as HTMLInputElement);
  if (currentIndex === -1 || inputs.length === 0) return;

  const nextIndex = event.shiftKey
    ? (currentIndex - 1 + inputs.length) % inputs.length
    : (currentIndex + 1) % inputs.length;

  event.preventDefault();
  inputs[nextIndex].focus();
  inputs[nextIndex].select();
};

export interface useTableStateProps<T extends MRT_RowData> {
  identifier: string;
  columns: MRT_ColumnDef<T>[];
  defaultColumnFilters?: MRT_ColumnFiltersState;
  defaultColumnFilterFns?: MRT_ColumnFilterFnsState;
  defaultColumnVisibility?: MRT_VisibilityState;
  defaultColumnSizing?: MRT_ColumnSizingState;
  defaultColumnOrder?: MRT_ColumnOrderState;
  defaultDensity?: MRT_DensityState;
  defaultGlobalFilter?: string;
  defaultSorting?: MRT_SortingState;
  defaultPagination?: MRT_PaginationState;
  defaultShowGlobalFilter?: boolean;
  defaultShowColumnFilters?: boolean;
}

export const useTableState = <T extends MRT_RowData>({
  identifier,
  columns,
  defaultColumnFilters,
  defaultColumnFilterFns,
  defaultColumnVisibility,
  defaultColumnSizing,
  defaultDensity,
  defaultGlobalFilter,
  defaultSorting,
  defaultPagination,
  defaultShowColumnFilters,
  defaultShowGlobalFilter,
}: useTableStateProps<T>) => {
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [writeBlock, setWriteBlock] = useState(true);
  useTimeout(() => setWriteBlock(false), 2000, {
    autoInvoke: true,
  });

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    defaultColumnFilters ?? []
  );
  const [columnFilterFns, setColumnFilterFns] = //filter modes
    useState<MRT_ColumnFilterFnsState>(
      defaultColumnFilterFns ??
        Object.fromEntries(
          columns.map(({accessorKey}) => [accessorKey, 'contains'])
        )
    ); //default to "contains" for all columns

  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    defaultColumnVisibility ?? {}
  );

  const [columnSizing, setColumnSizing] = useState<MRT_ColumnSizingState>(
    defaultColumnSizing ?? {}
  );

  const [density, setDensity] = useState<MRT_DensityState>(
    defaultDensity ?? 'md'
  );

  const [globalFilter, setGlobalFilter] = useState(defaultGlobalFilter ?? '');
  const [sorting, setSorting] = useState<MRT_SortingState>(
    defaultSorting ?? []
  );
  const [pagination, setPagination] = useState<MRT_PaginationState>(
    defaultPagination ?? {
      pageIndex: 0,
      pageSize: 10,
    }
  );

  const [showGlobalFilter, setShowGlobalFilter] = useState(
    defaultShowGlobalFilter ?? false
  );
  const [showColumnFilters, setShowColumnFilters] = useState(
    defaultShowColumnFilters ?? false
  );

  const leafColumns = getAllLeafColumnDefs(columns);
  const sortableKey = [
    ...leafColumns.filter(c => c.enableSorting !== false).map(getColumnId),
    ...(defaultSorting ?? []).map(s => s.id),
  ].join(ID_SEPARATOR);
  const filterableKey = [
    ...leafColumns.filter(c => c.enableColumnFilter !== false).map(getColumnId),
    ...(defaultColumnFilters ?? []).map(f => f.id),
  ].join(ID_SEPARATOR);
  const filterFnKey = [
    filterableKey,
    ...Object.keys(defaultColumnFilterFns ?? {}),
  ].join(ID_SEPARATOR);
  const sortableIds = useMemo(() => toIdSet(sortableKey), [sortableKey]);
  const filterableIds = useMemo(() => toIdSet(filterableKey), [filterableKey]);
  const filterFnIds = useMemo(() => toIdSet(filterFnKey), [filterFnKey]);

  const validSorting = useMemo(
    () => pruneById(sorting, sortableIds),
    [sorting, sortableIds]
  );
  const validColumnFilters = useMemo(
    () => pruneById(columnFilters, filterableIds),
    [columnFilters, filterableIds]
  );
  const validColumnFilterFns = useMemo(
    () => pruneFilterFns(columnFilterFns, filterFnIds),
    [columnFilterFns, filterFnIds]
  );

  const setValidSorting = useCallback(
    (update: SetStateAction<MRT_SortingState>) =>
      setSorting(prev =>
        typeof update === 'function'
          ? update(pruneById(prev, sortableIds))
          : update
      ),
    [sortableIds]
  );
  const setValidColumnFilters = useCallback(
    (update: SetStateAction<MRT_ColumnFiltersState>) =>
      setColumnFilters(prev =>
        typeof update === 'function'
          ? update(pruneById(prev, filterableIds))
          : update
      ),
    [filterableIds]
  );
  const setValidColumnFilterFns = useCallback(
    (update: SetStateAction<MRT_ColumnFilterFnsState>) =>
      setColumnFilterFns(prev =>
        typeof update === 'function'
          ? update(pruneFilterFns(prev, filterFnIds))
          : update
      ),
    [filterFnIds]
  );

  const start = pagination.pageIndex * pagination.pageSize;
  const length = pagination.pageSize;
  const filters = validColumnFilters.map(value => ({
    id: value.id,
    values: [].concat(serializeFilterValue(value.value) as any),
  }));
  const filterFns = Object.entries(validColumnFilterFns)
    .map(value => ({
      id: value[0],
      value: value[1].toString(),
    }))
    .filter(i => i.id !== 'undefined');

  // Load state from local storage
  useEffect(() => {
    const savedState = localStorage.getItem(identifier + '_mrt_state');

    const parsedState = JSON.parse(savedState ?? '{}');

    if (parsedState.columnFilters) {
      const hydratedFilters = parsedState.columnFilters.map(
        (filter: {id: string; value: unknown}) => ({
          ...filter,
          value: hydrateFilterValue(filter.value),
        })
      );
      setColumnFilters(hydratedFilters);
    }
    if (parsedState.columnFilterFns) {
      setColumnFilterFns(parsedState.columnFilterFns);
    }
    if (parsedState.columnVisibility) {
      setColumnVisibility({
        ...defaultColumnVisibility,
        ...parsedState.columnVisibility,
      });
    }
    if (parsedState.columnSizing) {
      setColumnSizing(parsedState.columnSizing);
    }
    if (parsedState.density) {
      setDensity(parsedState.density);
    }
    if (parsedState.globalFilter) {
      setGlobalFilter(parsedState.globalFilter);
    }
    if (parsedState.sorting) {
      setSorting(parsedState.sorting);
    }
    if (parsedState.pagination) {
      setPagination(parsedState.pagination);
    }
    if (parsedState.showGlobalFilter) {
      setShowGlobalFilter(parsedState.showGlobalFilter);
    }
    if (parsedState.showColumnFilters) {
      setShowColumnFilters(parsedState.showColumnFilters);
    }

    setIsFirstRender(false);
  }, [identifier]);

  // Save state to local storage
  useEffect(() => {
    if (isFirstRender || writeBlock) return;

    const stateToSave = {
      columnFilters,
      columnFilterFns,
      columnVisibility,
      columnSizing,
      density,
      globalFilter,
      sorting,
      pagination,
      showGlobalFilter,
      showColumnFilters,
    };

    console.log('Write Table: ', stateToSave);
    localStorage.setItem(
      identifier + '_mrt_state',
      JSON.stringify(stateToSave)
    );
  }, [
    columnFilters,
    columnFilterFns,
    columnVisibility,
    columnSizing,
    density,
    globalFilter,
    sorting,
    pagination,
    showGlobalFilter,
    showColumnFilters,
    identifier,
  ]);

  const resetState = () => {
    localStorage.removeItem(identifier + '_mrt_state');
    window.location.reload();
  };

  return {
    start,
    length,
    filters,
    filterFns,
    columnFilters: validColumnFilters,
    setColumnFilters: setValidColumnFilters,
    columnVisibility,
    setColumnVisibility,
    columnSizing,
    setColumnSizing,
    columnFilterFns: validColumnFilterFns,
    setColumnFilterFns: setValidColumnFilterFns,
    globalFilter,
    setGlobalFilter,
    showColumnFilters,
    setShowColumnFilters,
    showGlobalFilter,
    setShowGlobalFilter,
    density,
    setDensity,
    sorting: validSorting,
    setSorting: setValidSorting,
    pagination,
    setPagination,
    resetState,
  };
};
export interface useTableProps<T extends MRT_RowData> {
  columnFilters: MRT_ColumnFiltersState;
  setColumnFilters: React.Dispatch<
    React.SetStateAction<MRT_ColumnFiltersState>
  >;
  columnFilterFns: MRT_ColumnFilterFnsState;
  setColumnFilterFns: React.Dispatch<
    React.SetStateAction<MRT_ColumnFilterFnsState>
  >;
  columnVisibility: MRT_VisibilityState;
  setColumnVisibility: React.Dispatch<
    React.SetStateAction<MRT_VisibilityState>
  >;
  columnSizing: MRT_ColumnSizingState;
  setColumnSizing: React.Dispatch<React.SetStateAction<MRT_ColumnSizingState>>;
  showGlobalFilter: boolean;
  setShowGlobalFilter: React.Dispatch<React.SetStateAction<boolean>>;
  showColumnFilters: boolean;
  setShowColumnFilters: React.Dispatch<React.SetStateAction<boolean>>;
  density: MRT_DensityState;
  setDensity: React.Dispatch<React.SetStateAction<MRT_DensityState>>;
  globalFilter: string;
  setGlobalFilter: React.Dispatch<React.SetStateAction<string>>;
  sorting: MRT_SortingState;
  setSorting: React.Dispatch<React.SetStateAction<MRT_SortingState>>;
  pagination: MRT_PaginationState;
  setPagination: React.Dispatch<React.SetStateAction<MRT_PaginationState>>;
  getRowId?: (
    originalRow: T,
    index: number,
    parentRow: MRT_Row<T>
  ) => string | undefined;
  rowSelection?: MRT_RowSelectionState;

  tableOptions: MRT_TableOptions<T>;
  totalCount: number;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error?: string;

  resetState: () => void;

  /** Callback to refresh table data. When provided, adds a refresh icon to the toolbar. */
  onRefresh?: () => void | Promise<unknown>;

  /** Extra actions to render at the start of the internal toolbar (before refresh and built-in toggle buttons). */
  renderToolbarExtraActions?: React.ReactNode;

  /** Hides the reset-table link, for ephemeral tables with no persisted state worth resetting. */
  hideResetAction?: boolean;
}
export const useTable = <T extends MRT_RowData>({
  tableOptions,
  totalCount,
  isLoading,
  isFetching,
  isError,
  error,
  columnFilters,
  setColumnFilters,
  columnFilterFns,
  setColumnFilterFns,
  columnVisibility,
  setColumnVisibility,
  columnSizing,
  setColumnSizing,
  globalFilter,
  setGlobalFilter,
  density,
  setDensity,
  showColumnFilters,
  setShowColumnFilters,
  showGlobalFilter,
  setShowGlobalFilter,
  sorting,
  setSorting,
  pagination,
  setPagination,
  getRowId,
  rowSelection,
  resetState,
  onRefresh,
  renderToolbarExtraActions,
  hideResetAction = false,
}: useTableProps<T>) => {
  const withFilterTabNav = (props?: object) => ({
    ...props,
    onKeyDown: handleFilterTabNavigation,
  });

  // Below sm the rows-per-page select and the first/last edge buttons stack the
  // footer into three rows; drop them so pagination stays one reachable row.
  const isNarrow = useMediaQuery('(max-width: 47.99em)', false, {
    getInitialValueInEffect: false,
  });
  // Default display columns eat ~150px of a 390px viewport before the first
  // data column, so the leading pill and name fall off screen.
  const narrowDisplayColumnOptions = isNarrow
    ? {
        'mrt-row-expand': {size: 36, minSize: 36},
        'mrt-row-select': {size: 36, minSize: 36},
        'mrt-row-actions': {size: 72, minSize: 72},
        ...tableOptions.displayColumnDefOptions,
      }
    : tableOptions.displayColumnDefOptions;

  const callerPaginationProps = tableOptions.mantinePaginationProps;
  const paginationProps =
    typeof callerPaginationProps === 'function'
      ? callerPaginationProps
      : {
          showRowsPerPage: !isNarrow,
          withEdges: !isNarrow,
          ...(callerPaginationProps as object),
        };

  const columnsWithDateFilterModes = tableOptions.columns?.map(column => {
    const isDateFilter = column.filterVariant === 'date';
    if (isDateFilter && !column.columnFilterModeOptions) {
      return {
        ...column,
        columnFilterModeOptions: [...DATE_FILTER_MODE_OPTIONS],
      };
    }
    return column;
  });

  const config = useMantineReactTable({
    ...tableOptions,
    columns: columnsWithDateFilterModes ?? tableOptions.columns,
    enableClickToCopy: tableOptions.enableClickToCopy ?? false,
    mantineCopyButtonProps: {
      style: {width: '100%'},
      ...tableOptions.mantineCopyButtonProps,
    },
    mantineFilterAutocompleteProps: withFilterTabNav({
      placeholder: '',
      ...(tableOptions.mantineFilterAutocompleteProps as object),
    }),
    mantineFilterCheckboxProps: withFilterTabNav({
      placeholder: '',
      ...(tableOptions.mantineFilterCheckboxProps as object),
    }),
    mantineFilterRangeSliderProps: withFilterTabNav({
      placeholder: '',
      ...(tableOptions.mantineFilterRangeSliderProps as object),
    }),
    mantineFilterSelectProps: withFilterTabNav({
      placeholder: '',
      ...(tableOptions.mantineFilterSelectProps as object),
    }),
    mantineFilterTextInputProps: withFilterTabNav({
      placeholder: '',
      ...(tableOptions.mantineFilterTextInputProps as object),
    }),
    mantineFilterMultiSelectProps: withFilterTabNav(
      tableOptions.mantineFilterMultiSelectProps as object
    ),
    mantineFilterDateInputProps: withFilterTabNav({
      placeholder: '',
      ...(tableOptions.mantineFilterDateInputProps as object),
    }),
    mantinePaginationProps: paginationProps,
    mantineTopToolbarProps: {
      className: classes['top-toolbar'],
      ...(tableOptions.mantineTopToolbarProps as object),
    },
    displayColumnDefOptions: narrowDisplayColumnOptions,
    mantineTableHeadCellProps: {
      className: classes['table-head-cell'],
      ...tableOptions.mantineTableHeadCellProps,
    },
    enableRowActions: tableOptions.enableRowActions ?? true,
    enableGlobalFilter: tableOptions.enableGlobalFilter ?? false,
    enableColumnFilterModes: tableOptions.enableColumnFilterModes ?? true,
    columnFilterModeOptions: tableOptions.columnFilterModeOptions ?? [
      'contains',
      'startsWith',
      'endsWith',
    ],
    enableColumnResizing: tableOptions.enableColumnResizing ?? true,
    defaultColumn: {
      minSize: 40,
      maxSize: 1000,
      size: 200,
      ...tableOptions.defaultColumn,
    },
    initialState: tableOptions.initialState ?? {showColumnFilters: true},
    manualFiltering: tableOptions.manualFiltering ?? true,
    manualPagination: tableOptions.manualPagination ?? true,
    manualSorting: tableOptions.manualSorting ?? true,
    mantineToolbarAlertBannerProps: isError
      ? {
          color: 'red',
          children: error ?? 'Error loading data',
        }
      : undefined,
    renderToolbarInternalActions:
      onRefresh || renderToolbarExtraActions
        ? ({table}: {table: MRT_TableInstance<T>}) => {
            const {
              enableGlobalFilter = false,
              enableColumnFilters = true,
              enableFilters = true,
              enableHiding = true,
              enableDensityToggle = true,
              enableFullScreenToggle = true,
            } = tableOptions;

            return (
              <>
                {renderToolbarExtraActions}
                {onRefresh && (
                  <Tooltip label="Refresh data">
                    <ActionIcon
                      onClick={onRefresh}
                      variant="subtle"
                      color="gray"
                      loading={isFetching}
                      aria-label="Refresh data">
                      <IconRefresh size={18} />
                    </ActionIcon>
                  </Tooltip>
                )}
                {enableGlobalFilter && (
                  <MRT_ToggleGlobalFilterButton table={table} />
                )}
                {enableColumnFilters && enableFilters && (
                  <MRT_ToggleFiltersButton table={table} />
                )}
                {enableHiding && <MRT_ShowHideColumnsButton table={table} />}
                {enableDensityToggle && (
                  <MRT_ToggleDensePaddingButton table={table} />
                )}
                {enableFullScreenToggle && (
                  <MRT_ToggleFullScreenButton table={table} />
                )}
              </>
            );
          }
        : tableOptions.renderToolbarInternalActions,
    onColumnFilterFnsChange: setColumnFilterFns,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    onDensityChange: setDensity,
    onGlobalFilterChange: setGlobalFilter,
    onShowColumnFiltersChange: setShowColumnFilters,
    onShowGlobalFilterChange: setShowGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getRowId: getRowId ?? tableOptions.getRowId,
    rowCount: totalCount,
    state: {
      columnFilterFns,
      columnFilters,
      globalFilter,
      columnVisibility,
      columnSizing,
      density,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isFetching,
      showColumnFilters,
      showGlobalFilter,
      sorting,
      rowSelection: rowSelection ?? {},
    },
  } as MRT_TableOptions<T>);

  const table = (
    <>
      <MantineReactTable table={config} />
      {!hideResetAction && (
        <Group justify="end">
          <Button
            onClick={resetState}
            size="xs"
            variant="subtle"
            color="gray"
            mt="xs">
            Problem? Click to Reset Table
          </Button>
        </Group>
      )}
    </>
  );

  return {
    table,
  };
};
